import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CreateGroupBody, GalleryGroupAccessLevel, GroupDto, UpdateGroupBody } from '@nusign/picflow-backend-types';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { userGroupsMaxAge } from '../consts';
import { AppConfigService } from '../providers/config.service';
import { DataLoaderService } from './data-loader.service';
import { GetAllService } from './get-all.service';

@Injectable({
  providedIn: 'root',
})
export class UserGroupService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private loader = inject(DataLoaderService);
  private getAllService = inject(GetAllService);

  public async getUserGroups(): Promise<GroupDto[]> {
    return this.loader.get(
      'groups',
      async () => {
        const resp = await lastValueFrom(this.http.get<{ data: GroupDto[] }>(this.config.apiEndpoint + '/groups'));
        return resp.data;
      },
      userGroupsMaxAge
    );
  }

  public async getUserGroup(id: string): Promise<GroupDto> {
    return this.loader.get(
      'groups/' + id,
      async () => {
        return await lastValueFrom(this.http.get<GroupDto>(this.config.apiEndpoint + '/groups/' + id));
      },
      userGroupsMaxAge
    );
  }

  public async createUserGroup(group: CreateGroupBody): Promise<GroupDto> {
    const resp = await lastValueFrom(this.http.post<GroupDto>(this.config.apiEndpoint + '/groups', group));
    this.loader.remove('groups');
    return resp;
  }

  public async updateUserGroup(groupId: string, params: UpdateGroupBody): Promise<GroupDto> {
    const resp = await lastValueFrom(this.http.patch<GroupDto>(this.config.apiEndpoint + '/groups/' + groupId, params));
    this.loader.removeFuzzy('groups');
    return resp;
  }

  public async deleteUserGroup(groupId: string): Promise<void> {
    await lastValueFrom(this.http.delete<void>(this.config.apiEndpoint + '/groups/' + groupId));
    this.loader.removeFuzzy('groups');
    this.loader.removeFuzzy('group_galleries');
  }

  public async getUserGroupUsers(groupId: string): Promise<string[]> {
    return this.loader.get('groups/' + groupId + '/users', async () => {
      const resp = await lastValueFrom(
        this.http.get<{ data: { user: string }[] }>(this.config.apiEndpoint + '/group_users?group=' + groupId)
      );
      return resp.data.map(u => u.user);
    });
  }

  public async addUserToGroup(groupId: string, userId: string): Promise<void> {
    await lastValueFrom(this.http.post<void>(`${this.config.apiEndpoint}/groups/${groupId}/users/${userId}`, {}));
    this.loader.removeFuzzy('groups');
  }

  public async removeUserFromGroup(groupId: string, userId: string): Promise<void> {
    await lastValueFrom(this.http.delete<void>(`${this.config.apiEndpoint}/groups/${groupId}/users/${userId}`));
    this.loader.removeFuzzy('groups');
  }

  public async getGalleryUserGroups(galleryId: string) {
    const allGroupGalleries = await this.loader.get('group_galleries', async () => {
      return firstValueFrom(
        this.getAllService.getAll<{ group: string; id: string; access: GalleryGroupAccessLevel; gallery: string }>(
          `${this.config.apiEndpoint}/group_galleries?gallery=${galleryId}`
        )
      );
    });

    return allGroupGalleries.filter(gg => gg.gallery === galleryId);
  }

  public async getAllFolderUserGroups() {
    return this.loader.get('group_folders', async () => {
      return firstValueFrom(
        this.getAllService.getAll<{ group: string; id: string; access: GalleryGroupAccessLevel; folder: string }>(
          `${this.config.apiEndpoint}/group_folders`
        )
      );
    });
  }

  public async getFolderUserGroups(folderId: string) {
    const allGroupFolders = await this.getAllFolderUserGroups();
    return allGroupFolders.filter(gg => gg.folder === folderId);
  }

  public async addUserGroupToGallery(
    groupId: string,
    galleryId: string,
    access: GalleryGroupAccessLevel
  ): Promise<void> {
    await lastValueFrom(
      this.http.post<void>(`${this.config.apiEndpoint}/groups/${groupId}/galleries/${galleryId}`, { access })
    );
    this.loader.removeFuzzy('group_galleries');
  }

  public async addUserGroupToFolder(groupId: string, folderId: string, access: GalleryGroupAccessLevel): Promise<void> {
    await lastValueFrom(
      this.http.post<void>(`${this.config.apiEndpoint}/groups/${groupId}/folders/${folderId}`, { access })
    );
    this.loader.removeFuzzy('group_folders');
  }

  public async updateGalleryUserGroupAccessLevel(
    groupGalleryId: string,
    access: GalleryGroupAccessLevel
  ): Promise<void> {
    await lastValueFrom(
      this.http.patch<void>(`${this.config.apiEndpoint}/group_galleries/${groupGalleryId}`, { access })
    );
    this.loader.removeFuzzy('group_galleries');
  }

  public async updateFolderUserGroupAccessLevel(groupFolderId: string, access: GalleryGroupAccessLevel): Promise<void> {
    await lastValueFrom(this.http.patch<void>(`${this.config.apiEndpoint}/group_folders/${groupFolderId}`, { access }));
    this.loader.removeFuzzy('group_folders');
  }

  public async removeUserGroupFromGallery(groupId: string, galleryId: string): Promise<void> {
    await lastValueFrom(this.http.delete<void>(`${this.config.apiEndpoint}/groups/${groupId}/galleries/${galleryId}`));
    this.loader.removeFuzzy('group_galleries');
  }

  public async removeUserGroupFromFolder(groupId: string, folderId: string): Promise<void> {
    await lastValueFrom(this.http.delete<void>(`${this.config.apiEndpoint}/groups/${groupId}/folders/${folderId}`));
    this.loader.removeFuzzy('group_folders');
  }
}
